import { useEffect, useState } from 'react';
import { useAppBadge } from '@/hooks/useAppBadge';
import { supabase } from '@/integrations/supabase/client';

/**
 * Componente global que gerencia o badge do app
 * Usa RPC get_unread_notification_count para contagem correta
 * considerando broadcasts, target_roles e notification_reads
 */
export const AppBadgeManager = () => {
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setTotalCount(0);
          return;
        }

        // Usar RPC que considera broadcasts + notification_reads + target_roles
        const { data: notificationCount, error: rpcError } = await supabase
          .rpc('get_unread_notification_count');

        if (rpcError) {
          console.debug('Error calling get_unread_notification_count:', rpcError);
          // Fallback para contagem simples
          const { count } = await supabase
            .from('notifications')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('read', false);
          setTotalCount(count || 0);
          return;
        }

        // Buscar tarefas não lidas
        const { count: taskActivityCount } = await supabase
          .from('task_activities')
          .select('*', { count: 'exact', head: true })
          .eq('target_user_id', user.id)
          .eq('read', false);

        const total = (Number(notificationCount) || 0) + (taskActivityCount || 0);
        setTotalCount(total);
      } catch (error) {
        console.debug('Error fetching badge counts:', error);
      }
    };

    fetchCounts();

    const notificationsChannel = supabase
      .channel('badge-notifications')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, () => fetchCounts())
      .subscribe();

    const taskActivitiesChannel = supabase
      .channel('badge-task-activities')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_activities' }, () => fetchCounts())
      .subscribe();

    const readsChannel = supabase
      .channel('badge-notification-reads')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notification_reads' }, () => fetchCounts())
      .subscribe();

    return () => {
      supabase.removeChannel(notificationsChannel);
      supabase.removeChannel(taskActivitiesChannel);
      supabase.removeChannel(readsChannel);
    };
  }, []);

  useAppBadge(totalCount);
  return null;
};
